import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { baseUrl } from '../../shared.js';

// Start + Return date pickers for a booking, shared by Create and Edit Booking.
//
// Replaces the MUI X Pro DateRangePicker (unlicensed, so it carried a watermark)
// with two free DatePickers. Booked days are disabled in both, and the Return
// picker cannot go before the start or past the next booked day after it, so a
// range that crosses another booking cannot be picked at all.
//
// Props:
//   itemNo        item whose bookings block dates
//   bookingId     the booking being edited; its own dates are NOT blocked
//   initialStart  YYYY-MM-DD or empty
//   initialEnd    YYYY-MM-DD or empty
//   disablePast   true on create
//   onChange      ({ startDate, returnDate }) as YYYY-MM-DD, '' while incomplete

export const keyOf = (d) => `${d.date()}-${d.month() + 1}-${d.year()}`; // matches the API's '%e-%c-%Y'
const parseKey = (k) => {
  const [day, month, year] = k.split('-').map(Number);
  return dayjs(new Date(year, month - 1, day));
};

// The API returns ONE comma-joined string. Matching against it with .includes()
// was a substring test, so '1-10-2026' read as booked whenever the 11th, 21st or
// 31st was. Split it into a set.
export function parseBlocked(json) {
  const raw = Array.isArray(json) && json[0] && typeof json[0].dates === 'string' ? json[0].dates : '';
  return new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
}

// The last day Return may be for a given start: the day before the first booked
// day after it, or null when nothing is booked after it.
export function latestEndFor(start, blocked) {
  let first = null;
  blocked.forEach((k) => {
    const d = parseKey(k);
    if (d.isAfter(start, 'day') && (!first || d.isBefore(first, 'day'))) first = d;
  });
  return first ? first.subtract(1, 'day') : null;
}

export default function BookingDateRange({ itemNo, bookingId, initialStart, initialEnd, disablePast = false, onChange }) {
  const [start, setStart] = useState(initialStart ? dayjs(initialStart) : null);
  const [end, setEnd] = useState(initialEnd ? dayjs(initialEnd) : null);
  const [blocked, setBlocked] = useState(() => new Set());
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!itemNo) return;
    let cancelled = false;
    const headers = {
      'username': localStorage.getItem('username'),
      'bToken': localStorage.getItem('bToken'),
      'bookingItem': itemNo,
    };
    if (bookingId) headers.bookingId = bookingId;
    fetch(baseUrl + 'bookingDates/list/', { method: 'GET', headers })
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        setBlocked(parseBlocked(json));
        setLoadError(false);
      })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [itemNo, bookingId]);

  const isBlocked = (d) => blocked.has(keyOf(d));

  const latestEnd = start && start.isValid() ? latestEndFor(start, blocked) : null;

  const endIsValid = (s, e) => {
    if (!s || !e || e.isBefore(s, 'day')) return false;
    const last = latestEndFor(s, blocked);
    return !(last && e.isAfter(last, 'day'));
  };

  const emit = (s, e) => {
    const ok = s && s.isValid() && e && e.isValid() && endIsValid(s, e) && !isBlocked(s);
    onChange && onChange({
      startDate: ok ? s.format('YYYY-MM-DD') : '',
      returnDate: ok ? e.format('YYYY-MM-DD') : '',
    });
  };

  const handleStart = (value) => {
    setStart(value);
    // A new start can leave the chosen return before it or across a booking.
    let nextEnd = end;
    if (value && value.isValid() && end && !endIsValid(value, end)) nextEnd = null;
    setEnd(nextEnd);
    emit(value, nextEnd);
  };

  const handleEnd = (value) => {
    setEnd(value);
    emit(start, value);
  };

  // A typed date can still land on a blocked day or outside the window, so say so.
  let problem = '';
  if (start && start.isValid() && isBlocked(start)) problem = 'The start date is already booked.';
  else if (start && end && end.isValid() && end.isBefore(start, 'day')) problem = 'Return must be on or after the start date.';
  else if (start && end && end.isValid() && latestEnd && end.isAfter(latestEnd, 'day')) {
    problem = `Another booking starts ${latestEnd.add(1, 'day').format('DD-MM-YYYY')}. Return by ${latestEnd.format('DD-MM-YYYY')}.`;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 1.5 }, my: 1 }}>
        <DatePicker
          label="Start Date"
          value={start}
          onChange={handleStart}
          format="DD-MM-YYYY"
          disablePast={disablePast}
          shouldDisableDate={isBlocked}
          slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
        />
        <DatePicker
          label="Return Date"
          value={end}
          onChange={handleEnd}
          format="DD-MM-YYYY"
          disabled={!start}
          minDate={start || undefined}
          maxDate={latestEnd || undefined}
          referenceDate={start || undefined}
          shouldDisableDate={isBlocked}
          slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
        />
      </Box>
      {problem ? <FormHelperText error sx={{ mt: -0.5, mb: 1 }}>{problem}</FormHelperText> : null}
      {loadError ? (
        <FormHelperText error sx={{ mt: -0.5, mb: 1 }}>Could not load existing bookings, so booked dates are not greyed out.</FormHelperText>
      ) : null}
    </LocalizationProvider>
  );
}
